/**
 * Copyright (c) 2022 XYLink Inc.
 *
 * XY Electron SDK Lib
 *
 * @summary 小鱼易连主SDK模块，实现加入会议、会议管理等操作；
 *
 * Created at     : 2020-06-10 15:44:10
 * Last modified  : 2025-01-06 19:07:40
 */

import Logger from './tools/logger';
import { throttle, argbToRgba, isBlackImg, getNuaInfo, TYPEOF } from './tools/utils';
import { isExist } from './tools/file';
import {
  IConfig,
  ILayout,
  IRoster,
  IFrame,
  IAudio,
  IVideo,
  IMakeCallInfo,
  ILoginState,
  ICallStateChanged,
  ILoginInfo,
  IVideoFrame,
  IModel,
  IConfInfo,
  IConfControl,
  IVideoProfile,
  IAudioMeterInfo,
  IStatistics,
  ICallInvited,
  IHowlingDetected,
  VideoState,
  VideoStatus,
  IMicMuted,
  ISubTitle,
  IInOutReminder,
  IEventMap,
  IReqParams,
  ILocalVideoStatus,
  IMasterMap,
  IMasterParams,
  IResponse,
  IAIFaceRecv,
  IAIFaceInfo,
  IRecordStateChange,
  TCallMode,
  RecordStatus,
  TFaceType,
  TCallState,
  IRestApiResult,
  IConfHost,
  IMeetingMuteQuery,
  LayoutModel,
  TemplateModel,
  CallMode,
  FaceType,
  SDKFECCCommand,
  ISignInfo,
  DeviceTypeMap,
  IDeviceList,
  ICurrentDevice,
  IDeviceItem,
  DeviceTypeKey,
  LanguageType,
  IAiCaptionInfo,
  IXYParams,
  IExtTokenParams,
  Locale,
  ContentStreamMode,
  ContentCaptureType,
  ISendContentParams,
  ShareContentState,
  IAppWindowCaptureState,
  IRegion,
  EventType,
  ICopywriting,
  IVersion,
  IMonitorThumbnail,
  IApp,
  IAppIcon,
  IAppThumbnail,
  ProcessType,
  VideoBeautyStyle,
  VideoFilterStyle,
  VirtualBgMode,
  IMonitorInfo,
  ILine,
  IReceiveLine,
  XYProxyInfo,
  XYProxyType,
  XYReturnResult,
} from './type/index';
import {
  CALL_STATE_MAP,
  CONNECT_STATE_MAP,
  LOGIN_STATE_MAP,
  DEFAULT_MAKE_CALL_INFO,
  EVENT,
  ACCEPT_VIRTUAL_IMG_TYPE,
  DEFAULT_MASTER_CONFIG,
  LocalPreviewResolution,
  SUCCESS_CODE,
  SDK_FACE_TYPE,
  SDK_CALL_MODE,
  ENUM_COPY_WRITING,
  platformTagMap,
} from './tools/enum';
import { SDK_ERROR_MAP, SDK_ERROR_MAP_V2, SERVER_ERROR_MAP } from './tools/error';
import { XYTimer } from './tools/interval';
import { getStatus } from './tools/utils';
import LayoutManager from './layout';
import { getDllPath, getLogPath, SDK_LOG_NAME } from './tools/helper';
import DeviceManager from './device';
import { locale, setLocale } from './tools/i18n';
import { Base64 } from './tools/base64';

const path = require('path');
const EventEmitter = require('events').EventEmitter;
const SdkWrapper = require('../../build/Release/xy-electron-sdk.node');
const os = require('os');
const platform = os.platform();

class XYRTC extends EventEmitter {
  public sdk: any;
  public static instance: XYRTC;
  // constructor config data
  public config: IConfig;
  private loginState: string;
  private connectState: string;
  private callState: TCallState;
  // log class
  private logger: Logger;
  // makeCall params
  private makeCallInfo: IMakeCallInfo;
  // mic status
  private audio: IAudio;
  // camera status
  private video: IVideo;
  // conf control rename
  private rename: string;
  // Login user info
  private loginInfo: ILoginInfo;
  // 是否有外接屏渲染stream
  private isExternal: boolean;
  // 是否开启外接DM模式
  private isSlaveMode: boolean;
  // external timer
  private externalTimers: any;
  // external callback
  private externalCb: any;
  // external interval
  private externalInterval: number;
  // Layout管理器
  private layoutManager: LayoutManager;
  // 设备管理器
  private deviceManager: DeviceManager;
  // 注册外接屏幕设备列表
  private remoteMasterMap: IMasterMap;
  // private remoteDeviceIdMap: IMasterMap;
  private localPreviewResolution: LocalPreviewResolution;
  // 人脸识别类型
  private faceType: string;
  // 本地是否需展示人脸识别信息
  private enableLocalFD: boolean;
  // 0空闲 1开启录制中 2正在录制中 3停止录制中 4暂停录制相关，暂时用不到
  private recordStatus: RecordStatus;
  // 是否时本地远端录制中
  private isSelfRecord: boolean | undefined;
  // 录制权限
  private confCanRecord: boolean;
  // 是否支持同传字幕
  private isSupportAiCaption: boolean;
  /** 要发送的内容共享数据 */
  private sendContentParams: ISendContentParams;
  /** 是否在共享 */
  private contentSharing = false;

  // 记录当前互动工具processType
  private interactiveToolProcessType: ProcessType;

  // 是否初始化完成
  private initialized = false;

  private onCameraTypeChange: (type) => void;
  private onMicrophoneTypeChange: (type) => void;
  private onSpeakerTypeChange: (type) => void;

  constructor(config) {
    super();
    const { sdkLogPath, sdkLogDir } = getLogPath(config.logPath || '', SDK_LOG_NAME);

    this.config = {
      httpProxy: 'sdkapi.xylink.com',
      // 入会是否自动静音
      muteAudio: false,
      // 入会是否自动关闭摄像头
      muteVideo: false,
      templateModel: TemplateModel.SPEAKER,
      ...config,
      dllPath: config.dllPath || '',
      // 布局模式：AUTO：自动布局，CUSTOM：自定义布局
      model: (config.model.toUpperCase() as IModel) || LayoutModel.AUTO,
      // 自动监听窗口resize事件，并重新计算layout尺寸
      autoResizeLayout: typeof config.autoResizeLayout === 'boolean' ? config.autoResizeLayout : true
    };

    if(config.hostAddress) {
      this.config.hostAddress = config.hostAddress || '';
      this.config.port = config.port || 0
    }

    console.log('in sdk init...');

    this.logger = new Logger(sdkLogDir, sdkLogPath, SDK_LOG_NAME, () => {
      console.log('logger init...');

      this.initSDKAll(sdkLogDir, sdkLogPath);
    });
  }

  private initSDKAll(sdkLogDir, sdkLogPath) {
    this.logger.log('OS Info:', {
      // 返回为其编译 Node.js 二进制文件的操作系统 CPU 架构
      // 可能的值为 'arm'、'arm64'、'ia32'、'mips'、'mipsel'、'ppc'、'ppc64'、's390'、's390x'、以及 'x64'
      arch: os.arch(),
      // 返回 uname(3) 返回的操作系统名称
      // 它在 Linux 上返回 'Linux'，在 macOS 上返回 'Darwin'，在 Windows 上返回 'Windows_NT'
      kernel: os.type(),
      // 返回标识为其编译 Node.js 二进制文件的操作系统平台的字符串
      // 可能的值为 'aix'、'darwin'、'freebsd'、'linux'、'openbsd'、'sunos'、以及 'win32'
      pl: os.platform(),
      // 以整数形式返回系统内存总量（以字节为单位）
      totalmem: os.totalmem(),
      // 返回当前用户的主目录的字符串路径
      homeDir: os.homedir(),
      home: process.env.HOME,
      // 以整数形式返回空闲的系统内存量（以字节为单位）
      totalMem: os.totalmem(),
      // 以整数形式返回空闲的系统内存量（以字节为单位）
      freeMem: os.freemem(),
      // 返回包含有关每个逻辑 CPU 内核的信息的对象数组
      cpu: os.cpus(),
      versions: process.versions,
    });

    this.logger.log('Log Path: ', { sdkLogPath, sdkLogDir });
    this.logger.log('User Config:', this.config);

    XYRTC.instance = null;

    this.logger.log('Merged Config:', this.config);

    console.log('start load node');

    this.sdk = SdkWrapper.getInstance();

    console.log('end load node');

    // 初始化日志
    this.sdk.initialize({ sdkLogPath, sdkLogDir });

    this.layoutManager = new LayoutManager(this);

    this.initNemoSDK();

    this.logger.log('Electron SDK Version:', this.getVersion());

    this.logger.log('SDK Config:', this.config);

    this.initEventHandler();

    this.loginState = 'Logouted';
    this.connectState = 'Disconnected';
    this.callState = 'Not_In_Call';

    this.makeCallInfo = DEFAULT_MAKE_CALL_INFO;

    this.isExternal = false;
    this.isSlaveMode = false;
    // external timers
    this.externalTimers = {};
    this.externalCb = null;
    this.externalInterval = 66.66;
    this.audio = 'unmute';
    this.video = 'unmuteVideo';
    this.rename = '';

    this.remoteMasterMap = {};
    this.localPreviewResolution = LocalPreviewResolution.init;

    // 人脸相关
    this.faceType = 'NONE';
    this.enableLocalFD = false;

    // 录制相关
    this.recordStatus = RecordStatus.IDLE;
    this.isSelfRecord = false;
    this.confCanRecord = true;

    this.onDeviceTypeChange = this.onDeviceTypeChange.bind(this);
    this.onCameraTypeChange = throttle(this.onDeviceTypeChange, 1000);
    this.onMicrophoneTypeChange = throttle(this.onDeviceTypeChange, 1000);
    this.onSpeakerTypeChange = throttle(this.onDeviceTypeChange, 1000);

    this.deviceManager = new DeviceManager(this, this.logger);

    this.isSupportAiCaption = false;

    this.interactiveToolProcessType = ProcessType.NONE;

    this.statisticsSysSource();

    this.initialized = true;
  }

  /**
   * 获取 XYRTC 实例
   * 每次入会时，都需要调用此方法获取实例，仅针对当此会议有效！
   *
   * @static
   */
  static getXYInstance(config: IConfig) {
    if (!this.instance) {
      this.instance = new XYRTC(config || {});
    }

    return this.instance;
  }

  statisticsTimer = 0;

  private statisticsSysSource = () => {
    clearInterval(this.statisticsTimer);

    this.statisticsTimer = window.setInterval(() => {
      const CPUUsage = process.cpuUsage();
      const memoryUsage = process.memoryUsage();
      this.logger.log('process resource usage', { CPUUsage, memoryUsage });
    }, 60000);
  };

  /**
   * 销毁 XYRTC 实例
   */
  static clearXYInstance() {
    this.instance = null;
  }

  /**
   * 获取SDK初始化配置数据
   *
   * @public
   * @returns { IConfig } sdk init config params
   */
  public getConfig() {
    return this.config;
  }

  /**
   * 获取登录用户信息
   *
   * @public
   * @returns { ILoginInfo }
   */
  public getLoginInfo() {
    return this.loginInfo;
  }

  /**
   * 获取呼叫会议信息
   *
   * @public
   * @returns { IMakeCallInfo }
   */
  public getMakeCallInfo(): IMakeCallInfo {
    return this.makeCallInfo;
  }

  /**
   * 获取人脸识别类型
   *
   * @returns faceType
   */
  public getFaceType() {
    return this.faceType;
  }

  /**
   * 获取本地音频、视频状态
   *
   * @public
   * @returns { ILocalVideoStatus }
   */
  public getLocalVideoStatus(): ILocalVideoStatus {
    return {
      audio: this.audio,
      video: this.video,
      rename: this.rename,
    };
  }

  /**
   * 获取Electron SDK version
   *
   * @returns {IVersion}
   */
  public getVersion(): IVersion {
    this.logger.log('[invoke] getVersion');

    const cppVersion = this.sdk.getSDKVersion();

    return {
      version: 'SDK_PKG_VERSION',
      buildTime: 'SDK_BUILD_TIME',
      cppVersion,
      info: 'SDK_PKG_VERSION - build on SDK_BUILD_TIME',
    };
  }

  /**
   * Get dlls path and Init Nemo SDK
   *
   * @private
   */
  private initNemoSDK() {
    const { dllPath, httpProxy, clientId = '', clientSecret = '', appType = 'electron_sdk', enableSharingIndicator = true } = this.config;

    const currentDirection = this.sdk.getCurrentDirectory();
    const dllName = platform === 'win32' ? 'NemoSDK.dll' : 'libnemoSDK.dylib';
    const paths = getDllPath(dllPath, currentDirection, dllName);
    const { sdkDllPath, sdkProjectPath } = paths;

    this.config.sdkDllPath = sdkDllPath;
    this.config.sdkProjectPath = sdkProjectPath;

    this.sdk.InitSDK({
      sdkDllPath,
      sdkProjectPath,
      httpProxy,
      clientId,
      clientSecret,
      appVersion: 'SDK_PKG_VERSION',
      platformTag: platformTagMap[process.platform] || 'windows',
      appType,
      enableSharingIndicator,
      hostAddress: this.config.hostAddress || '',
      port: this.config.port || 0
    });

    this.setUserAgent();

    this.setLocale(Locale.zh_CN);
  }

  /**
   * setProxy函数延迟处理
   *
   * @param { XYProxyType } proxyType 代理类型
   * @param { XYProxyInfo }  proxyInfo 代理详情
   * @returns { XYReturnResult } 状态码数据
   */
  private setProxyDelay(proxyType: XYProxyType, proxyInfo: XYProxyInfo) {
    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await this.setProxy(proxyType, proxyInfo);
        resolve(result);
      }, 20);
    });
  }

  /**
   * 设置代理
   * 注意：代理只能在登录前设置
   *
   * @since 3.10.6
   * @param { XYProxyType } proxyType 代理类型
   * @param { XYProxyInfo }  proxyInfo 代理详情
   * @returns { XYReturnResult } 状态码数据
   */
  public async setProxy(proxyType: XYProxyType, proxyInfo: XYProxyInfo) {
    this.logger.log('invoke setProxy function: ', proxyType, proxyInfo);

    if (!this.initialized) {
      return this.setProxyDelay(proxyType, proxyInfo);
    }

    const { None, HTTP, UDP, SOCK5 } = XYProxyType;

    if (this.loginState !== 'Logouted') {
      this.logger.log('Proxies can only be set before logging in');
      return getStatus('XYSDK:960120');
    }

    if (![None, HTTP, UDP, SOCK5].includes(proxyType)) {
      this.logger.log('setProxy function params proxyType is error');
      return getStatus('XYSDK:960101', { proxyType });
    }

    if (
      !TYPEOF.isObject(proxyInfo) ||
      (proxyInfo.hostAddress && !TYPEOF.isString(proxyInfo.hostAddress)) ||
      (proxyInfo.port && !TYPEOF.isNumber(proxyInfo.port)) ||
      (proxyInfo.username && !TYPEOF.isString(proxyInfo.username)) ||
      (proxyInfo.password && !TYPEOF.isString(proxyInfo.password))
    ) {
      this.logger.log('setProxy function params proxyInfo is error');
      return getStatus('XYSDK:960101', { proxyInfo });
    }

    const { hostAddress = '', port = 0, username = '', password = '' } = proxyInfo;

    this.sdk.setProxy(proxyType, { hostAddress, port, username, password });

    return getStatus(SUCCESS_CODE);
  }

  /**
   * 设置SDK国际化语言
   *
   * @param { Locale } locale 语言
   */
  public setLocale(language: Locale) {
    setLocale(language);
    this.sdk?.updateLocale(locale);
  }

  /**
   * 设置底层接口n-ua，供数据埋点使用
   */
  private setUserAgent() {
    const nua = getNuaInfo(this.config.appType);

    this.logger.log('[invoke] setUserAgent nua=', nua);

    this.sdk.setUserAgent(nua);
  }

  // public setRemoteServer(server: string) {
  //   this.logger.log("[SDK]invoke setRemoteServer: ", server);

  //   this.sdk.setRemoteServer(server);
  // }

  /**
   * Get Local data
   *
   * @public
   * @returns { ILayout } local data
   */
  public getLocal() {
    const local = this.layoutManager.getLocal();

    return local;
  }

  /**
   * 获取已经注册的屏幕列表数据
   *
   * @public
   * @returns { remoteMasterMap } remoteMasterMap list
   */
  public getRemoteMasterMap() {
    return this.remoteMasterMap;
  }

  /**
   * Get video frame buffer data by c++
   *
   * @public
   * @param { string } sourceId - 用户资源id
   * @param { boolean } isContent - 是否是共享Content内容
   * @returns { IFrame } Video frame buffer data
   */
  public getVideoFrame(sourceId: string, isContent: boolean) {
    return this.sdk.getVideoFrame(sourceId, isContent) as IFrame;
  }

  /**
   * 获取可用设备列表数据
   *
   * @public
   * @param { DeviceTypeKey } type 设备类型："camera" | "microphone" | "speaker"
   * @property camera: 摄像头
   * @property microphone: 麦克风
   * @property speaker: 扬声器
   * @returns { Promise<IDeviceList> } Promise
   */
  public getDeviceList(type?: DeviceTypeKey): Promise<IDeviceList | IDeviceItem[]> {
    return new Promise((resolve, reject) => {
      this.logger.log('invoke get device list function: ', type);

      if (type && !DeviceTypeKey[type]) {
        this.logger.log('Get devices params type is error');
        return reject(getStatus('XYSDK:960101', { type }));
      }

      const deviceList = this.deviceManager.getDeviceList(type);
      return resolve(deviceList);
    });
  }

  /**
   * 获取当前使用的设备
   *
   * @param {DeviceTypeKey} [type]
   * @return {*}  {(Promise<ICurrentDevice | string>)}
   * @memberof XYRTC
   */
  public getCurrentDevice(type?: DeviceTypeKey): Promise<ICurrentDevice | string> {
    return new Promise((resolve, reject) => {
      const currentDevice = this.deviceManager.getCurrentDevice(type);
      return resolve(currentDevice);
    });
  }

  /**
   * 获取设备列表 仅供sdk内部使用
   *
   * @param {DeviceTypeMap} devType
   * @return {*}
   * @memberof XYRTC
   */
  public getDevListEx(devType: DeviceTypeMap) {
    return this.sdk.getDevList(devType);
  }

  /**
   * 开始音频采集，麦克风
   */
  public startAudioCapture() {
    this.logger.log('startAudioCapture');

    this.sdk.startAudioCapture();
  }

  /**
   * 结束音频采集，麦克风
   */
  public stopAudioCapture() {
    this.logger.log('stopAudioCapture');

    this.sdk.stopAudioCapture();
  }

  /**
   * 选择设备切换 仅供sdk内部使用
   *
   * @param {DeviceTypeMap} devType
   * @param {string} devId
   * @memberof XYRTC
   */
  public chooseDevEx(devType: DeviceTypeMap, devId: string) {
    this.sdk.chooseDev(devType, devId);

    if (devType === DeviceTypeMap.microphone) {
      const isOnHold = this.layoutManager.getIsOnHold();

      if (!isOnHold && this.callState === 'Connected') {
        this.startAudioCapture();
      }
    }
  }

  /**
   * 切换输入/输出设备
   *
   * @public
   * @param { DeviceTypeKey } type 设备类型："camera" | "microphone" | "speaker"
   * @param { string } devId 设备id
   * @returns { Promise<string> } Promise
   */
  public switchDevice(type: DeviceTypeKey, devId: string) {
    return new Promise((resolve, reject) => {
      this.logger.log('invoke set device function: ', { type, devId });

      const types = ['camera', 'microphone', 'speaker'];

      if (!type || !types.includes(type)) {
        this.logger.log('switchDevice: params type is error');
        return reject(getStatus('XYSDK:960101', { type }));
      }

      const deviceList = this.deviceManager.getDeviceList();
      const current = deviceList[type].find((item) => item.devId === devId);

      if (!current) {
        this.logger.log('switchDevice: params devId is error');
        return reject(getStatus('XYSDK:960101', { devId }));
      }

      this.switchDeviceSync(type, devId);

      resolve('set device successfully');
    });
  }

  /**
   * 切换设备同步方法 sdk内部使用
   *
   * @param {DeviceTypeKey} type
   * @param {string} devId
   * @memberof XYRTC
   */
  public switchDeviceSync(type: DeviceTypeKey, devId: string | undefined) {
    this.logger.log('invoke switchDeviceSync: ', { type, devId });
    this.deviceManager.switchDevice(type, devId);

    if (type === 'camera' && (this.callState === 'Connected' || this.callState === 'Connecting') && this.video === 'unmuteVideo') {
      this.sdk.muteCamera(true);
      this.stopCamera();

      // 可能用户拔了摄像头
      if (!devId) {
        return;
      }

      setTimeout(() => {
        this.sdk.muteCamera(false);
        this.startCamera();
      }, 100);
    }
  }

  /**
   * 切换分页
   * @param page 分页 大于等于0
   * @returns Promise
   */
  public switchPage(page: number) {
    this.logger.log('switchPage func invoke, with page=', page);

    return new Promise((resolve, reject) => {
      const res = this.layoutManager.switchPage(page);
      if (res) {
        reject(res);
      } else {
        resolve(getStatus(SUCCESS_CODE));
      }
    });
  }

  /**
   * 订阅部分参会者数据，会按照分页模式优先返回这些订阅的数据
   *
   * @public
   * @param { string[] } uriList callerUri列表数据，callerUri 需要从 Server SDK Api 上获取所有的参会成员数据
   * @returns { Promise<string> } Promise
   */
  public subscribeRoster(uriList: string[]) {
    return new Promise((resolve, reject) => {
      this.logger.log('invoke subscribe roster function: ', uriList);

      if (uriList.length <= 0) {
        reject(getStatus('XYSDK:960101', { uriList }));
      }

      this.sdk.subscribeRoster(uriList);

      resolve(getStatus(SUCCESS_CODE));
    });
  }

  /**
   * 检查是否支持同传字幕
   *
   * @public
   * @return { Promise } Promise
   */
  public checkAiCaptionSupport() {
    this.logger.log('checkAiCaptionSupport func invoke');

    return new Promise((resolve) => {
      this.sdk.checkAiCaptionSupport();
      resolve(getStatus(SUCCESS_CODE));
    });
  }

  /**
   * 同传字幕切换语言
   *
   * @public
   * @param {LanguageType} language 切换的字幕语言/关闭字幕
   * @return { Promise } Promise
   */
  public setCaptionLanguage(language: LanguageType) {
    this.logger.log('setCaptionLanguage func invoke: ', language);

    return new Promise((resolve, reject) => {
      if (!this.isSupportAiCaption) {
        return reject(getStatus('XYSDK:960109', { language }));
      }
      if (!LanguageType[language]) {
        return reject(getStatus('XYSDK:960101', { language }));
      }

      this.sdk.setSubtitleLanguage(language);

      resolve(getStatus(SUCCESS_CODE));
    });
  }

  /**
   * 停止所有的渲染器
   *
   * @public
   */
  public stopAllVideoRender() {
    this.logger.log('stopAllVideoRender func invoke');

    this.layoutManager.stopAllVideoRender();
  }

  /**
   * 【外接屏幕】上报视频流数据
   *
   * @private
   */
  private cbStreamBySourceId() {
    const arr = Array.prototype.slice.call(arguments);
    const id = arr[0];
    const sourceId = arr[1];
    const isContent = arr[2];
    const videoFrame = this.getVideoFrame(sourceId, isContent);

    if (videoFrame.hasData && this.externalCb) {
      this.externalCb({ id, sourceId, videoFrame });
    }
  }

  /**
   * 【外接屏幕】设置渲染定时器
   *
   * @public
   * @param { ILayout } item 设置roster item数据
   */
  public setExternalTimer(item: ILayout) {
    if (!this.isExternal) {
      return;
    }

    const { sourceId, roster, id } = item;
    const { isContent = false, state = VideoState.Received } = roster;

    this.logger.log('set external timer: ', item);

    // 视频开启时，启动定时器
    if (id && state === VideoState.Received) {
      if (!this.externalTimers[id]) {
        const timer = XYTimer.setInterval(id, this.cbStreamBySourceId.bind(this, id, sourceId, isContent), this.externalInterval);

        this.externalTimers[id] = {
          timer,
        };
      }
    } else {
      // 否则清理资源
      this.clearExternalTimer(id);
    }

    this.logger.log('set external timer:', this.externalTimers);
  }

  /**
   * 【外接屏幕】清理External定时器
   *
   * @public
   * @param { string } key sourcedId资源id
   */
  public clearExternalTimer(key: string) {
    if (this.externalTimers[key]) {
      XYTimer.clearInterval(key);

      delete this.externalTimers[key];
    }
  }

  /**
   * 【外接屏幕】上报Local视频流数据
   *
   * @public
   * @param { (id, sourceId, roster) => void } callback 数据流回调
   */
  public startLocalExternal(callback) {
    this.logger.log('start local external function invoke');

    if (this.isExternal) {
      this.logger.warn('Invalid invoke!');

      return 'Invalid invoke!';
    }

    this.isExternal = true;
    this.externalCb = callback;
    const localInfo = this.layoutManager.getLocal();

    this.setExternalTimer(localInfo);
  }

  /**
   * 【外接屏幕】停止上报Local视频流数据
   *
   * @public
   */
  public stopLocalExternal() {
    this.logger.log('stop external function');

    if (!this.isExternal) {
      this.logger.warn('Invalid invoke!');

      return 'Invalid invoke!';
    }

    const timerKeys = Object.keys(this.externalTimers);

    timerKeys.map((id: string) => {
      let timer = this.externalTimers[id];

      if (timer) {
        this.logger.log('close external timer', id);

        this.clearExternalTimer(id);
        delete this.externalTimers[id];
      }
    });

    // 停止所有外接屏幕推送流
    this.isExternal = false;
    this.externalTimers = {};
    this.externalCb = null;

    this.logger.log('stop external timers:', this.externalTimers);
  }

  /**
   * 绑定sourceId和canvas，代理渲染视频
   *
   * @public
   * @param sourceId sourceId，通过回调：EVENT.RTC_EVENT_VIDEO_STREAMS 返回给业务
   * @param canvasEle canvas id，由第三方提供canvas元素id，后续由sdk接管使用webgl渲染视频
   */
  public setVideoRender(sourceId: string, canvasEle: HTMLCanvasElement | string) {
    this.logger.log('setVideoRender func:', sourceId, typeof canvasEle);

    return this.layoutManager.setVideoRender(sourceId, canvasEle);
  }

  /**
   * 发起呼叫
   *
   * @param number 会议号
   * @param meetingPwd 入会密码，如果没有，则填写空字符
   * @param displayName 入会昵称，本地和远端显示此名称
   * @param muteVideo 是否关闭摄像头入会
   * @param muteAudio 是否静音入会
   * @param callMode 会议模式 "AudioVideo":音视频; "AudioOnly":语音模式
   */
  public makeCall(
    meetingNumber: string = '',
    meetingPwd: string = '',
    displayName: string = '',
    muteVideo: boolean = true,
    muteAudio: boolean = true,
    config?: {
      callMode?: string;
      isMonitor?: boolean;
      meetingId?: string;
    }
  ) {
    this.logger.log('[invoke]makeCall func: ', {
      meetingNumber,
      meetingPwd,
      displayName,
      muteVideo,
      muteAudio,
      config,
    });

    if (displayName.length > 64) {
      this.logger.log('The displayName can be up to 128 characters long');

      return getStatus('XYSDK:960110', { displayName });
    }

    const { callMode: initialCallMode = 'AudioVideo', isMonitor = false, meetingId = '' } = config || {};

    // 在会中，则禁止呼叫
    if (!(this.callState === 'Not_In_Call' || this.callState === CALL_STATE_MAP['1'])) {
      this.logger.log('The meeting is connected, please end call');

      return getStatus('XYSDK:960107');
    }

    // 没有登录&连接成功，则禁止发起呼叫
    if (this.loginState !== 'Logined') {
      this.logger.log('Not login in');

      return getStatus('XYSDK:960106');
    }

    if (meetingNumber && displayName) {
      this.makeCallInfo = {
        meetingNumber,
        displayName,
        meetingPwd,
        meetingId,
      };

      let callMode = initialCallMode;

      // 监会需静音入会
      if (isMonitor) {
        muteAudio = true;
        callMode = 'MonitorMode';
      }

      // 会议模式
      const meetingCallMode = SDK_CALL_MODE[callMode] || SDK_CALL_MODE['AudioVideo'];

      this.config = {
        ...this.config,
        muteAudio,
        muteVideo,
        isMonitor,
      };
      this.audio = muteAudio ? 'mute' : 'unmute';
      this.video = muteVideo ? 'muteVideo' : 'unmuteVideo';

      const local = this.layoutManager.getLocal();
      // 语音模式，local state===7
      const nextLocal = {
        ...local,
        roster: {
          ...local.roster,
          state: meetingCallMode === 4 ? 7 : local.roster.state,
        },
      };

      this.layoutManager.setLocal(nextLocal);

      if (this.config.autoResizeLayout && this.config.model === LayoutModel.AUTO) {
        this.layoutManager.initWindowResizeEvent();
      }

      this.sdk.makeCall({
        meetingNumber,
        meetingPwd,
        displayName,
        muteVideo,
        muteAudio,
        callMode: meetingCallMode,
        meetingId,
      });

      return getStatus('XYSDK:963000');
    } else {
      return getStatus('XYSDK:963005');
    }
  }

  /**
   * 开启摄像头
   */
  public startCamera() {
    this.logger.log('[invoke]start camera');

    this.sdk.startCamera();
  }

  /**
   * 关闭摄像头
   */
  public stopCamera() {
    this.logger.log('[invoke]stop camera');

    this.sdk.stopCamera();
  }

  /**
   * 切换布局模式
   *
   * @param model 默认是AUTO布局，可以设定自动布局（AUTO）和自定义布局（CUSTOM）
   */
  public switchModel(model: IModel) {
    this.logger.log('[invoke]switchModel:', model);

    this.config.model = model.toUpperCase() as IModel;
  }

  /**
   * 获取主持会议的相关配置信息和状态， 在ConfHostChanged中回调
   *
   * @param conferenceNo 会议室号码
   * @param scheduledMeetingId 预约会议id
   */
  public checkHostMeetingPermission(conferenceNo: string, scheduledMeetingId = '') {
    this.logger.log('[invoke] checkHostMeetingPermission: ', conferenceNo, scheduledMeetingId);

    this.sdk.checkHostMeetingPermission(conferenceNo, scheduledMeetingId);
  }

  /**
   * 切换麦克风状态
   *
   * true为静音，设置后远端听不到当前设备的声音
   * false为非静音，设置后，远端可以听到当前设备的声音
   *
   * @param status boolean
   */
  public muteMic(status: boolean) {
    this.logger.log('mute mic: ', status);
    if (typeof status !== 'boolean') {
      this.logger.log('status must is bool value');

      return getStatus('XYSDK:960101', { status });
    }

    if (status) {
      this.audio = 'mute';
    } else {
      this.audio = 'unmute';
    }

    this.layoutManager.setLocalLayoutStatus();
    this.sdk.muteMic(status);
  }

  /**
   * 切换摄像头状态
   *
   * true为关闭摄像头，关闭后，远端不显示此设备画面，并显示当前设备视频暂停状态
   * false为开启摄像头，开启后，远端可以看到当前设备的画面
   *
   * @param status boolean
   */
  public muteCamera(status: boolean) {
    this.logger.log('[invoke]mute camera: ', status);
    this.sdk.muteCamera(status);

    if (typeof status !== 'boolean') {
      this.logger.log('status must is bool value');

      return getStatus('XYSDK:960101', { status });
    }

    if (status) {
      this.video = 'muteVideo';
      this.stopCamera();
    } else {
      this.video = 'unmuteVideo';
      this.startCamera();
    }

    this.layoutManager.setLocalLayoutStatus();
    this.layoutManager.updateLocalRenderer(status);
  }

  /**
   * 切换布局
   * @param templateModel 布局模式 CONTENT 5种 NORMAL 2种 共7种
   * @returns
   */
  public switchLayout(templateModel?: TemplateModel) {
    this.logger.log('[invoke]switchLayout: ', templateModel);

    return new Promise((resolve, reject) => {
      const res = this.layoutManager.switchLayout(templateModel);

      if (res?.code === SUCCESS_CODE) {
        resolve(res);
      } else {
        reject(res);
      }
    });
  }

  /**
   * 遥控摄像头方法，外部调用
   *
   * @public
   * @param {string} callUri 终端callUri
   * @param {SDKFECCCommand} command 遥控指令
   * @param {number} angle 步长角度
   * @memberof XYRTC
   */
  public farEndHardwareControl(callUri: string, command: SDKFECCCommand, angle: number) {
    this.logger.log('[invoke]farEndHardwareControl: ', callUri, command, angle);

    if (this.sdk) {
      this.sdk.farEndHardwareControl(callUri, command, angle);
    }
  }

  /**
   * 重新计算Layout布局数据
   *
   * 触发机制：当页面窗口变化/Container Dom大小变化时，可主动调用同时SDK更新Layout信息
   * 建议：需配置autoResizeLayout为false关闭内部的事件，避免重复计算；
   *
   * @memberof XYRTC
   */
  public updateLayoutSize() {
    this.logger.log('[invoke] updateLayoutSize');

    if (this.config.model !== LayoutModel.AUTO) {
      this.logger.log('updateLayoutSize: ', 'Current is custom model, not support update layout size');

      return getStatus('XYSDK:960104');
    }
    this.layoutManager.onDocumentChange();
  }

  /**
   * 结束会议
   *
   * 自动释放设备资源，会时必须主动调用
   */
  public endCall() {
    this.logger.log('endCall');

    this.stopCamera();
    this.sdk.endCall();
    this.layoutManager.destroy();

    // 停止外接定时器
    if (this.isExternal) {
      this.stopLocalExternal();
    }

    this.callState = CALL_STATE_MAP['1'] as TCallState;
    this.makeCallInfo = DEFAULT_MAKE_CALL_INFO;
    this.audio = 'unmute';
    this.video = 'unmuteVideo';
    this.rename = '';

    this.faceType = 'NONE';
    this.enableLocalFD = false;

    this.isSupportAiCaption = false;

    this.localPreviewResolution = LocalPreviewResolution.init;

    // 重置录制状态
    this.resetRecordStatus();

    // 退会停止声音采集
    this.stopAudioCapture();
  }

  /**
   * 重置云端录制状态
   */
  private resetRecordStatus() {
    // 录制相关
    this.recordStatus = RecordStatus.IDLE;
    this.confCanRecord = true;
    this.isSelfRecord = false;
  }

  /**
   * 区域共享后更新区域坐标
   *
   * @param {IRegion} region 区域坐标
   * @returns
   */
  public updateContentCaptureRegion(region: IRegion) {
    this.logger.log('updateContentCaptureRegion: ', region);

    if (!TYPEOF.isObject(region)) {
      return getStatus('XYSDK:960101', { region });
    }

    const { x, y, w, h } = region;

    if (typeof x !== 'number' || typeof y !== 'number' || typeof w !== 'number' || typeof h !== 'number') {
      return getStatus('XYSDK:960101', { region });
    }

    this.sdk.updateContentCaptureRegion('', {
      top: y,
      bottom: y + h,
      left: x,
      right: x + w,
    });
  }

  /**
   * 共享内容
   *
   * @param {ISendContentParams} params 共享内容参数
   * @returns
   */
  public startSendContent(params: ISendContentParams) {
    this.logger.log('start send content:', params);

    if (!TYPEOF.isObject(params)) {
      return getStatus('XYSDK:960101');
    }

    const { contentStreamMode, contentCaptureType, contentInfo } = params;

    if (typeof contentStreamMode !== 'number' || !(contentStreamMode in ContentStreamMode)) {
      return getStatus('XYSDK:960101', { contentStreamMode });
    }

    if (typeof contentCaptureType !== 'number' || !(contentCaptureType in ContentCaptureType)) {
      return getStatus('XYSDK:960101', { contentCaptureType });
    }

    if (!TYPEOF.isObject(contentInfo) || !TYPEOF.isString(contentInfo.source)) {
      return getStatus('XYSDK:960101', { contentInfo });
    }

    this.logger.log('startShareContent', contentStreamMode, contentCaptureType, contentInfo);

    this.sendContentParams = params;

    this.sdk.startSendContent(contentStreamMode, false, contentInfo.bEnableAnnotation || false);

    return getStatus(SUCCESS_CODE);
  }

  /**
   * 暂停content采集
   */
  public pauseContentCapture() {
    this.logger.log('pause content capture');

    this.sdk.pauseContentCapture();
  }

  /**
   * 恢复content采集
   */
  public resumeContentCapture() {
    this.logger.log('resume content capture');

    this.sdk.resumeContentCapture();
  }

  /**
   * 结束分享桌面
   */
  public stopSendContent() {
    this.logger.log('stop send content');

    this.sdk.stopContentCapture();
    this.sdk.stopSendContent();
  }

  /**
   * 开始标注
   */
  public startAnnotation() {
    this.logger.log('[invoke] startAnnotation');

    this.sdk.startAnnotation();
  }

  /**
   * 关闭标注
   */
  public stopAnnotation() {
    this.logger.log('[invoke] stopAnnotation');

    this.sdk.stopAnnotation();
  }

  /**
   * 清空标注
   */
  public cleanAnnotation() {
    this.logger.log('[invoke] cleanAnnotation');

    this.sdk.cleanAnnotation();
  }

  /**
   * 发送线，content接受者标注时用到
   *
   * @param line
   */
  public sendAnnotationLine(line: ILine) {
    this.logger.log('[invoke] sendAnnotationLine', line);

    line.ended = line.ended ?? true;
    line.getAllLines = line.getAllLines ?? false;
    line.calluri = line.calluri ?? '';
    line.uuid = line.uuid ?? '';

    this.logger.log('[invoke] sendAnnotationLine1', line);

    this.sdk.sendAnnotationLine(line);
  }

  /**
   * 设置本地预览视频分辨率
   */
  public setLocalPreviewResolution(res: LocalPreviewResolution) {
    this.logger.log('set local preview resolution:', res);

    if (!TYPEOF.isNumber(res)) {
      return 'resolution is not support!';
    }

    const confInfo = this.layoutManager.getConfInfo();
    const { participantCount } = confInfo;
    let nextLocalRes = this.localPreviewResolution;

    // 超过两个人（local+远端）并且主动设置本地画面分辨率超过360，则重置为180P画面
    if (participantCount >= 2 && res > LocalPreviewResolution.normal360P) {
      nextLocalRes = LocalPreviewResolution.normal360P;
    } else {
      nextLocalRes = res;
    }

    this.logger.log('match resolution: ', {
      nextLocalRes,
      previousRes: this.localPreviewResolution,
    });

    if (nextLocalRes !== this.localPreviewResolution) {
      this.logger.log('really set local preview resolution:', nextLocalRes);

      this.localPreviewResolution = nextLocalRes;
      this.sdk.setLocalPreviewResolution(this.localPreviewResolution);
    }
  }

  /**
   * AE Audio dump
   * 入会前设置，dump本次呼叫中的音频数据供分析噪声，回声使用
   */
  public enableAudioDump() {
    this.logger.log('start enableAudioDump');

    this.sdk.enableAudioDump();
  }

  /**
   * 是否开启小鱼回声消除功能(默认启用)
   *
   * @public
   * @param { boolean } enable 是否开启AE模块，默认是启用
   */
  public enableAECMode(enable: boolean) {
    this.logger.log('start enableAECMode', enable);

    this.sdk.enableAECMode(!!enable);
  }

  /**
   * 小鱼账号登录 (旧)
   *
   * @deprecated 已废弃 但不影响老客户调用，建议使用新登录方式：loginXYAccount
   *
   * @param { string } userName 用户名、邮箱
   * @param { string } password 密码
   * @returns { Object } 登录结果
   */
  public login(userName: string = '', password: string = '') {
    this.logger.log('xylink login', userName);

    if (userName && password) {
      // 强密码登录false
      this.sdk.login(userName, password, false);

      return getStatus(SUCCESS_CODE, {}, 'Logging...Please check the result in the callback event login-state');
    } else {
      return getStatus('XYSDK:960101', { userName, password });
    }
  }

  /**
   * 第三方登录接口
   *
   * 注意：v1.0.4版本登录方式进行了安全升级，但需要传authCode和isTempUser两个参数。
   * 为了兼容旧的登录方式，新增的两个参数作为选填项
   * 1. 如果用户没有传authCode，则仍然使用旧的登录方式登录，
   * 2. 如果传了authCode，则使用新的更安全的方式登录
   *
   * @param {string} extID 企业ID
   * @param {string} extUserId 第三方用户ID
   * @param {string} displayName  第三方用户名
   * @param {string} authCode 授权码 v1.0.4新增
   * @param {boolean} isTempUser 是否临时用户 v1.0.4新增
   */
  public loginExternalAccount(extID: string, extUserId: string, displayName: string, authCode?: string, isTempUser?: boolean) {
    this.logger.log('[invoke] external login: ', {
      extID,
      extUserId,
      displayName,
      authCode,
      isTempUser,
    });

    if (!extID || !displayName || !extUserId) {
      this.logger.log('loginExternalAccount params error: extID or displayName or extUserId is invalid!');

      return getStatus('XYSDK:960101', {
        extID,
        displayName,
        extUserId,
      });
    }

    if (!/^[0-9a-zA-Z_-]{1,50}$/.test(extUserId)) {
      this.logger.log('loginExternalAccount params error: ', 'extUserId can only contain numbers, letters, _ and -, and the length cannot be greater than 50');

      return getStatus('XYSDK:960111', { extUserId });
    }

    if (authCode) {
      this.sdk?.loginExtUserId(extID, displayName, extUserId, authCode, isTempUser || false);
    } else {
      this.sdk?.loginExtUser(extID, extUserId, displayName);
    }

    return getStatus(SUCCESS_CODE, {}, 'Logging...Please check the result in the callback event login-state');
  }

  /**
   * 建行授权码登录
   *
   * @param {string} extID 企业id
   * @param {string} extUserId 用户id
   * @param {string} displayName 用户名称
   * @param {string} oauthCode 授权码
   * @param {boolean} isTempUser 是否临时账户
   * @param {string} channelId 渠道id
   * @return {*}
   */
  public loginWithAuthCode(extID: string, extUserId: string, displayName: string, oauthCode: string, isTempUser: boolean, channelId: string) {
    this.logger.log('[invoke] loginWithAuthCode: ', extID, extUserId, displayName, oauthCode, isTempUser, channelId);

    const reg = /^[0-9a-zA-Z_-]{1,50}$/;

    const requiredField = {
      extID,
      extUserId,
      oauthCode,
      channelId,
    };

    for (const key of Object.keys(requiredField)) {
      if (!requiredField[key]) {
        this.logger.log('loginWithAuthCode params error: ', `${key} is required`);

        return getStatus('XYSDK:960101', {
          [key]: requiredField[key],
        });
      }
    }

    if (extUserId) {
      // extUserId 只能是 数字、字母、_-, 长度不能超过50
      if (!reg.test(extUserId)) {
        this.logger.log('loginWithAuthCode params error:', 'extUserId can only contain numbers, letters, _ and -, and the length cannot be greater than 50');

        return getStatus('XYSDK:960111', { extUserId });
      }
    }

    this.sdk && this.sdk.loginWithAuthCode(extID, extUserId, displayName, oauthCode, isTempUser || false, channelId);

    return getStatus(SUCCESS_CODE, {}, 'Logging...Please check the result in the callback event login-state');
  }

  /**
   * 小鱼账号登录 (新)
   *
   * @param { IXYParams } params 账户信息
   */
  public loginXYAccount(params: IXYParams) {
    this.logger.log('[invoke] loginXYAccount: ', params);

    const { extID, countryCode = '+86', account, password } = params;

    if (extID && countryCode && account && password) {
      this.sdk.loginXYAccount(extID, countryCode, account, password);

      return getStatus(SUCCESS_CODE, {}, 'Logging...Please check the result in the callback event login-state');
    } else {
      return getStatus('XYSDK:960101', {
        extID,
        countryCode,
        account,
        password,
      });
    }
  }

  /**
   * 三方企业token登录
   *
   * @param { IExtTokenParams } params 三方企业token信息
   */
  public loginExtToken(params: IExtTokenParams) {
    this.logger.log('[invoke] loginExtToken: ', params);

    const { extID, token } = params;

    if (extID && token) {
      this.sdk.loginExtToken(extID, token);

      return getStatus(SUCCESS_CODE, {}, 'Logging...Please check the result in the callback event login-state');
    } else {
      return getStatus('XYSDK:960101', { extID, token });
    }
  }

  // /**
  //  * 三方企业ID方式登录
  //  *
  //  * @param {IExtUserIdParams} extUserIdParams 三方企业ID账户信息
  //  */
  // public loginExtUserId(extUserIdParams: IExtUserIdParams) {
  //   this.logger.log('[invoke] : ', extUserIdParams);

  //   const { extID, displayName, extUID, authCode } = extUserIdParams;

  //   if (extID && displayName && extUID && authCode) {
  //     this.sdk.loginExtUserId(extID, displayName, extUID, authCode);
  //     const code = CUSTOM_ERROR_CODE.ERROR_LOGGING;
  //     return getStatus(code, CUSTOM_ERROR_MAP[code]);
  //   } else {
  //     this.emit(
  //       EVENT.RTC_EVENT_LOGIN_STATE,
  //       getStatus('XYSDK:960101', 'extID or displayName or extUID or authCode is invalid!')
  //     );
  //   }
  // }

  /**
   * 检测是否支持webgl渲染
   *
   * @returns {boolean} 返回是否支持webgl渲染状态
   */
  public isSupportWebGL() {
    let status = true;
    const canvas = document.createElement('canvas');
    const options = {
      alpha: false,
      depth: false,
      stencil: false,
      antialias: false,
      preferLowPowerToHighPerformance: true,
    };
    let gl;

    canvas.width = 1;
    canvas.height = 1;

    try {
      gl = canvas.getContext('webgl', options) || canvas.getContext('experimental-webgl', options);
    } catch (e) {
      status = false;
    }

    status = !!gl;

    this.logger.log('Support webgl: ', status);

    return status;
  }

  /**
   * 退出登录
   */
  public logout() {
    this.logger.log('logout account');

    this.sdk && this.sdk.logout();
  }

  /**
   * 获取本地麦克风能量波动
   */
  public getMicPeakMeter() {
    return this.sdk && this.sdk.getMicPeakMeter();
  }

  /**
   * 获取通话统计信息
   */
  public getStatistics(): IStatistics {
    return this.sdk && this.sdk.getStatistics();
  }

  /**
   * 设置mic声音峰值范围[0, 100]
   */
  public setMicVolume(volume: number) {
    this.sdk && this.sdk.setMicVolume(volume);
  }

  /**
   *  获取本地麦克风音量范围值，范围[0, 100]
   */
  public getMicVolume() {
    return (this.sdk && this.sdk.getMicVolume()) || 0;
  }

  /* 麦克风检测 */
  public enableListenerTest(enable: boolean) {
    this.sdk && this.sdk.enableMicTest(enable);
  }

  /**
   * 获取本地扬声器能量波动
   * @returns
   */
  public getSpeakerPeakMeter(): number {
    return this.sdk?.getSpeakerPeakMeter();
  }

  /**
   * 设置本地扬声器能量
   *
   * @param { number } volume
   */
  public setSpeakerVolume(volume: number) {
    this.sdk?.setSpeakerVolume(volume);
  }

  /**
   * 获取本地扬声器当前能量
   *
   * @returns
   */
  public getSpeakerVolume(): number {
    return this.sdk?.getSpeakerVolume();
  }

  /**
   * 获取远端音频能量
   *
   * @returns
   */
  public getAudioPeakMeters(): IAudioMeterInfo[] {
    return this.sdk?.getAudioPeakMeters();
  }

  /**
   * 获取屏幕缩略图
   *
   * @param {string} monitorName 屏幕标识
   */
  public getMonitorThumbnail(monitorName: string): IMonitorThumbnail {
    this.logger.log(`[invoke] getMonitorThumbnail: monitorName=${monitorName}`);

    const monitor: IMonitorThumbnail = this.sdk?.getMonitorThumbnail(monitorName);
    const { hasData, buffer } = monitor;

    if (hasData && buffer) {
      monitor.buffer = argbToRgba(buffer, true);
    }
    return monitor;
  }

  /**
   * 获取显示器列表
   *
   * @returns {string[]} monitorName
   */
  public getMonitorList(): string[] {
    this.logger.log('[invoke] getMonitorList');

    return this.sdk?.getMonitorList();
  }

  /**
   * 获取显示器列表，包含monitorName和位置信息
   *
   * @returns {IMonitorInfo[]}
   */
  public getMonitorInfos(): IMonitorInfo[] {
    this.logger.log('[invoke] getMonitorInfos');

    return this.sdk?.getMonitorInfos();
  }

  /**
   * 获取 app 列表
   */
  public getAppList(): IApp[] {
    this.logger.log('[invoke] getAppList');

    return this.sdk?.getTopLevelAppList();
  }

  /**
   * 获取 app 缩略图数据
   *
   * @param {number} hwnd app 窗口句柄
   */
  public getAppThumbnail(hwnd: number): IAppThumbnail {
    this.logger.log(`[invoke] getMonitorThumbnail, hwnd=${hwnd}`);

    const app: IAppThumbnail = this.sdk?.getAppThumbnail(hwnd);
    const { hasData, buffer } = app;

    if (hasData && buffer) {
      // 检查图片是不是全黑的，是的话 hasData=false，应用层可以使用 appIcon 数据
      if (isBlackImg(buffer)) {
        app.hasData = false;
      } else {
        app.buffer = argbToRgba(buffer, true);
      }
    }
    return app;
  }

  /**
   * 获取 App icon 图标数据
   *
   * @param {number} hwnd app 窗口句柄
   */
  public getAppIcon(hwnd: number): IAppIcon {
    this.logger.log(`[invoke] getAppIcon: hwnd=${hwnd}`);

    const icon: IAppIcon = this.sdk?.getAppIcon(hwnd);
    const { hasData, buffer } = icon;

    if (hasData && buffer) {
      icon.buffer = argbToRgba(buffer, false);
    }
    return icon;
  }

  /**
   * 开启远端音频能量峰值计量
   */
  public setAudioPeakMeterEnabled(enable: boolean) {
    this.logger.log(`setAudioPeakMeterEnabled: enable=${enable}`);
    this.sdk?.setAudioPeakMeterEnabled(enable);
  }

  /**
   * 开始播放测试文件用于扬声器检测
   *
   * @param { string } wavFile wav文件
   * @param { number } loopTimes 循环次数， 0：循环播放
   */
  public startPlayWavFile(wavFile: string, loopTimes: number = 0) {
    this.sdk?.startPlayWavFile(wavFile, loopTimes);
  }

  /**
   * 结束播放测试文件用于扬声器检测
   */
  public stopPlayWavfile() {
    this.sdk?.stopPlayWavfile();
  }

  /* 设置视频配置，呼叫前设置有效 */
  public setVideoProfile(videoProfile: IVideoProfile) {
    this.logger.log('Invoke set video profile:', videoProfile);

    this.sdk?.setVideoProfile(videoProfile);
  }

  /**
   * 录制文件地址配置
   */
  public setLocalRecordingsFilePath(filePath) {
    const { localStorePath = '' } = filePath || {
      localStorePath: '',
    };

    return new Promise((resolve, reject) => {
      this.logger.log('invoke set local recordings file path: ', filePath);

      if (!localStorePath) {
        this.logger.log('localStorePath is required');

        return reject(
          getStatus('XYSDK:960101', {
            localStorePath,
          })
        );
      }

      this.sdk?.setLocalRecordingsFilePath(filePath);

      resolve(getStatus(SUCCESS_CODE));
    });
  }

  /**
   * 开启本地录制
   *
   * @public
   */
  public startLocalRecord() {
    this.sdk?.startLocalRecord();
  }

  /**
   * 停止本地录制
   *
   * @public
   */
  public stopLocalRecord() {
    this.sdk?.stopLocalRecord();
  }

  /**
   * 开启云端录制
   */
  public startCloudRecord() {
    this.logger.log('[invoke] start cloud record');
    const { ACTING_BY_OTHERS, PAUSE_BY_OTHERS, ACTING, DISABLE } = RecordStatus;

    // autoStop传参没有经过qa测试，暂时注掉
    // if(autoStop !== undefined && typeof autoStop !== 'boolean'){
    //   this.logger.log('start cloud record fail: autoStop=', autoStop);

    //   return getStatus('XYSDK:960101', { autoStop });
    // }

    if ([ACTING_BY_OTHERS, PAUSE_BY_OTHERS].includes(this.recordStatus) && !this.isSelfRecord) {
      this.logger.log('start cloud record fail: other Term has started recording');

      return getStatus('XYSDK:960108');
    }

    if (!this.confCanRecord || this.recordStatus === DISABLE) {
      this.logger.log('start cloud record fail: no recording permission');

      return getStatus('XYSDK:960113');
    }

    if ([ACTING, PAUSE_BY_OTHERS, ACTING_BY_OTHERS].includes(this.recordStatus) && this.isSelfRecord) {
      this.logger.log('start cloud record fail: recording in progress');

      return getStatus('XYSDK:960114');
    }

    this.sdk?.startCloudRecord(true);
  }

  /**
   * 停止云端录制
   */
  public stopCloudRecord() {
    this.logger.log('[invoke] stop cloud record');
    const { ACTING, ACTING_BY_OTHERS, PAUSE_BY_OTHERS } = RecordStatus;

    if (!([ACTING, ACTING_BY_OTHERS, PAUSE_BY_OTHERS].includes(this.recordStatus) && this.isSelfRecord)) {
      this.logger.log('stop cloud record fail: no recording in progress');

      return getStatus('XYSDK:960115');
    }

    this.sdk?.stopCloudRecord();
  }

  /**
   * 举手发言
   */
  public sendSpeakingRequest() {
    this.sdk?.sendSpeakingRequest();
  }

  /**
   * 发言结束
   */
  public sendSpeakingEnd() {
    this.sdk?.sendSpeakingEnd();
  }

  /**
   * 取消举手
   */
  public cancelSpeakingRequest() {
    this.sdk?.cancelSpeakingRequest();
  }

  /**
   * 签到
   *
   * @public
   * @param {string} url webViewUrl
   * @param {string} questionnaireId 业务id
   * @memberof XYRTC
   */
  public signIn(url: string, questionnaireId: string) {
    this.logger.log('[invoke] signIn url and questionnaireId', url, questionnaireId);

    this.sdk?.signIn(url, questionnaireId);
  }

  /**
   * 获取会控URL
   *
   * @param { string } confNum
   */
  public getConfMgmtUrl(confNum: string = this.makeCallInfo.meetingNumber) {
    this.logger.log('[invoke] getConfMgmtUrl confNum:', confNum);

    this.sdk?.getConfMgmtUrl(confNum);
  }

  /**
   * 全屏请流
   * @param id rosterId
   */
  public forceFullScreen(id: string) {
    this.logger.log('[invoke] force full screen params id:', id);

    const forceFullScreenId = this.layoutManager.getForceFullScreenId();
    const layout = this.layoutManager.getLayout();

    return new Promise((resolve, reject) => {
      // 自定义模式不支持forceFullScreen
      if (this.config.model !== LayoutModel.AUTO) {
        this.logger.log('forceFullScreen: ', 'Current is custom model, not support force full screen');

        return reject(getStatus('XYSDK:960105'));
      }

      if (id) {
        // 已经forceFullScreen了
        if (id === forceFullScreenId) {
          return resolve(getStatus(SUCCESS_CODE));
        }

        const index = layout.findIndex((item: ILayout) => item.id === id);

        // 判断id有效性
        if (index === -1) {
          this.logger.log('Force full screen params id is error');
          return reject(getStatus('XYSDK:960101', { id }));
        }
      }
      this.layoutManager.setForceFullScreenId(id);
      this.layoutManager.calcRequestLayout();
      this.emit(EVENT.RTC_EVENT_FORCE_FULL_SCREEN, id);

      resolve(getStatus(SUCCESS_CODE));
    });
  }

  /**
   * 是否广播本地电子铭牌
   *
   * @param { boolean } enable
   */
  public broadcastEletronicBadge(enable = true) {
    this.logger.log('[invoke] broadcast electronic badge:', enable);

    this.sdk?.broadcastEletronicBadge(!!enable);
  }

  /**
   * 是否显示远端人脸信息
   *
   * @param { TFaceType } faceType Detect:扫描模式 EletronicBadge：电子铭牌
   * @param { boolean } enable 是否显示
   */
  public enableFaceDetectMode(faceType: TFaceType = FaceType.EletronicBadge, enable = true) {
    this.logger.log('[invoke] enable face detect mode:', faceType, enable);

    if (faceType !== FaceType.Detect && faceType !== FaceType.EletronicBadge) {
      faceType = FaceType.EletronicBadge;
    }

    this.faceType = enable ? faceType : 'NONE';
    // 开启远端人脸识别，无论扫描还是铭牌都传0，sdk内部无需区分铭牌和扫描，位置信息都一样，demo层需区分铭牌和扫描的样式
    this.sdk?.enableFaceDetectMode(SDK_FACE_TYPE[FaceType.Detect], enable);

    this.updateFaceDetectMode();

    if (this.config.model === LayoutModel.AUTO) {
      this.layoutManager.calcRequestLayout();
    }
  }

  public updateFaceDetectMode() {
    // 显示人脸识别, 本地上大屏或对称布局有Local. true
    if (this.faceType !== 'NONE' && this.enableLocalFD === false) {
      const layout = this.layoutManager.getLayout();
      const local = this.layoutManager.getLocal();

      const isLocal = layout.find((item) => item?.id === local?.id);

      if (isLocal) {
        this.logger.log('[invoke] update enable face detect mode:', this.faceType, true);

        this.sdk?.enableFaceDetectMode(SDK_FACE_TYPE[FaceType.Detect], true);
        this.enableLocalFD = true;
      }
    }

    const layout = this.layoutManager.getLayout();

    // 显示人脸识别且本地不在大屏时候 false
    if (this.templateModel === 'SPEAKER' && layout.length > 1 && this.faceType !== 'NONE' && this.enableLocalFD === true) {
      this.logger.log('[invoke] update enable face detect mode:', this.faceType, false);

      this.sdk?.enableFaceDetectMode(SDK_FACE_TYPE[FaceType.Detect], false);
      this.enableLocalFD = false;
    }
  }

  /**
   * 获取人脸信息详情
   *
   * @deprecated
   * @param { number[] } faceIds
   * @returns
   */
  public getFaceInfo(faceIds: number[]) {
    this.logger.log('[invoke] get face info:', faceIds);

    if (Object.prototype.toString.call(faceIds) !== '[object Array]') {
      this.logger.log('FaceIds must be array!');

      return getStatus('XYSDK:960101', { faceIds });
    }

    const ids = faceIds.filter((id) => Number(id) && Number(id) !== -1);

    this.sdk?.getFaceInfo(ids);
  }
  /**
   * 切换语音模式
   *
   * @param { TCallMode } callMode AudioOnly 语音模式， AudioVideo 音视频模式
   */
  public switchCallMode(callMode: TCallMode) {
    this.logger.log('[invoke] switch call mode:', callMode);

    const isAudioMode = callMode === CallMode.AudioOnly;
    const audioMute = this.audio === 'mute';
    const videoMute = this.video === 'muteVideo';

    this.sdk?.switchCallMode(isAudioMode, audioMute, videoMute);
  }

  /* 会中邀请h323设备
  /**
   *
   * @param { string } key 号码
   */
  public sendDtmf(key, remoteUri = '') {
    this.logger.log('[invoke] sendDtmf:', key);

    const { callUri } = this.layoutManager.getLocal().roster;

    if (this.callState === 'Connected') {
      this.sdk?.sendDtmf(remoteUri || callUri, key);
    }
  }

  /**
   * 释放音频焦点，会外设备检测完成之后，如果需要释放音频的话调用
   */
  public releaseAudioFocus() {
    this.logger.log('[invoke] releaseAudioFocus');

    // c++ 3.8后 releaseAudioFocus 不再生效，这里换成 stopAudioCapture 以兼容老版本
    this.sdk?.stopAudioCapture();
  }

  /**
   * 日志上传
   *
   * @param { string } comments 反馈内容
   */
  public logUpload(comments: string = '') {
    this.logger.log('[invoke] log upload:', comments);

    // 3.8 新增了日志类型 0日志、1崩溃信息、2audioDump, 这里写死日志
    this.sdk?.logUpload(String(comments), 0);
  }

  /**
   * 主屏幕启动外接屏幕渲染模式，默认屏幕id是：LocalDeviceId
   *
   * @param { IMasterParams } params 设置主屏幕启动服务信息
   */
  public startMaster(params?: IMasterParams): Promise<any> {
    return new Promise((resolve, reject) => {
      this.logger.log('startMaster func invoke.', params);

      if (!TYPEOF.isObject(params)) {
        this.logger.log('params need receive object value');

        const msgInfo = getStatus('XYSDK:960101', { params });

        return reject(msgInfo);
      }

      const { deviceID = 'LocalDeviceId' } = params || {};
      const masterConfig = Object.assign({}, DEFAULT_MASTER_CONFIG, {
        serial: 'EXXylink',
        deviceID,
      });

      this.logger.log('start master merge params: ', masterConfig);

      if (this.sdk) {
        this.isSlaveMode = true;

        this.sdk.startMaster(masterConfig);
      } else {
        this.logger.warn('not init sdk instance');
      }

      return resolve(getStatus(SUCCESS_CODE));
    });
  }

  /**
   * 停止外接屏幕渲染
   *
   * @public
   * @returns { Promise }
   */
  public stopMaster() {
    return new Promise((resolve) => {
      this.logger.log('stopMaster func invoke.');

      // 清理资源
      this.isSlaveMode = false;
      this.remoteMasterMap = {};
      this.sdk.stopMaster();

      // 自动布局会自动根据模板请流操作
      if (this.config.model === LayoutModel.AUTO) {
        this.layoutManager.calcRequestLayout();
      }

      resolve(getStatus(SUCCESS_CODE));
    });
  }

  /**
   * Set log level
   * level: "INFO" | "DEBUG" | "WARNING" | "ERROR" | "NONE"
   *
   * @public
   */
  public setLogLevel(level: 'INFO' | 'DEBUG' | 'WARNING' | 'ERROR' | 'NONE') {
    this.logger.setLogLevel(level);
  }

  /**
   * 设置本地视频镜像翻转
   *
   * @param { boolean } bFlip 是否反转镜像
   */
  public setLocalPreviewFlip(bFlip: boolean) {
    this.logger.log('setLocalPreviewFlip: ', { bFlip });

    this.sdk?.setLocalPreviewFlip(bFlip);
  }

  /**
   * 发送消息
   *
   * @param { keyof IEventMap } key 消息key
   * @param { any } data 消息内容
   */
  public emitMessage(key: keyof IEventMap, data: any) {
    this.logger.log('emit message: ', { key, data });

    this.emit(key, data);
  }

  /**
   * [CUSTOM]请求参会成员质量和分辨率数据的画面
   *
   * @param reqList 请流列表
   * @param maxViewCount 每页最多可请求多少数据流，最大可设定位8位
   * @param pageIndex：页码，指定请求第几页数据
   */
  public requestLayout(reqList: any = [], maxViewCount: number = 8, pageIndex: number = 0) {
    this.logger.log('[invoke] request layout:', {
      reqList,
      maxViewCount,
      pageIndex,
    });

    this.layoutManager.requestLayout(reqList, maxViewCount, pageIndex);
  }

  /**
   * [SDK]请求参会成员质量和分辨率数据的画面
   *
   * @param { IReqParams } params 请流参数
   */
  public requestLayoutEx(params: IReqParams) {
    this.sdk.requestLayout(params);
  }

  /**
   * 设置美颜效果
   *
   * @param { VideoBeautyStyle } style 美颜类型
   * @param { number } level 美颜等级，值在 0-100 之间
   */
  public setVideoBeautyEffect(style: VideoBeautyStyle, level: number) {
    this.logger.log('setVideoBeautyEffect:', { style, level });

    this.sdk?.setVideoBeautyEffect(style, level);
  }

  /**
   * 设置滤镜效果
   *
   * @param { VideoFilterStyle } style 滤镜类型
   * @param { number } level 滤镜等级，值在 0-100 之间
   */
  public setVideoFilterEffect(style: VideoFilterStyle, level: number) {
    this.logger.log('setVideoFilterEffect:', { style, level });

    this.sdk?.setVideoFilterEffect(style, level);
  }

  /**
   * 设置虚拟背景模式
   *
   * @param { VirtualBgMode } mode 虚拟背景模式
   */
  public setVirtualBgMode(mode: VirtualBgMode) {
    this.logger.log('setVirtualBgMode:', { mode });

    this.sdk?.setVirtualBgMode(mode);
  }

  /**
   * 设置虚拟背景图片
   *
   * @param { string } imgPath 虚拟背景图片的文件路径
   */
  public setVirtualBgImage(imgPath: string) {
    this.logger.log('setVirtualBgImage:', imgPath);

    const ext = path.extname(imgPath);

    const isAcceptImgType = ACCEPT_VIRTUAL_IMG_TYPE.includes(ext);

    if (!isAcceptImgType) {
      const status = getStatus('XYSDK:963006');

      this.logger.error('setVirtualBgImage error', status);
      return status;
    }

    if (!isExist(imgPath)) {
      const status = getStatus('XYSDK:963007');

      this.logger.error('setVirtualBgImage error', status);
      return status;
    }

    this.sdk?.setVirtualBgImage(imgPath);
  }

  /**
   * 设备变化回调事件
   *
   * @private
   * @param deviceType
   */
  private onDeviceTypeChange(deviceType) {
    this.emit(EVENT.RTC_MEDIA_DEVICE_EVENT, deviceType);
  }

  /**
   * register c++ bridge event message
   *
   * @private
   */
  private initEventHandler() {
    this.logger.log('init c++ event handler');

    // login state
    this.sdk.registerEvent(EVENT.RTC_EVENT_LOGIN_STATE, (e: ILoginState) => {
      this.logger.log('Get sdk event login-state: ', e);

      const { state, info, error } = e;

      this.loginState = LOGIN_STATE_MAP[state] || LOGIN_STATE_MAP['3'];
      // 如果登录成功，需要记录登录信息
      if (this.loginState === 'Logined') {
        this.loginInfo = info;
      }

      this.emit(EVENT.RTC_EVENT_LOGIN_STATE, {
        state: this.loginState,
        error: SDK_ERROR_MAP_V2[error] ?? error,
        info: this.loginInfo,
      });
    });

    this.sdk.registerEvent(EVENT.RTC_EVENT_CONNECT_STATE, (e: number) => {
      this.logger.log('Get sdk event connect-state: ', e);

      this.connectState = CONNECT_STATE_MAP[e] || CONNECT_STATE_MAP['0'];

      this.emit(EVENT.RTC_EVENT_CONNECT_STATE, this.connectState);
    });

    // call state
    this.sdk.registerEvent(EVENT.RTC_EVENT_CALL_STATE, (e: ICallStateChanged) => {
      this.logger.log('Get sdk event call-state: ', e);

      const { isOnhold, waitingReason } = e;
      const muteAudio = this.audio === 'mute';
      const muteVideo = this.video === 'muteVideo';
      const reason = e.state === 1 ? SERVER_ERROR_MAP[locale][e.reason] || e.reason : e.reason;

      // 已经在会中，说明是并非呼叫触发的callState, 这里是为了监听是否是onhold更新
      const hasInMeeting = CALL_STATE_MAP[e.state] === 'Connected' && this.callState === 'Connected';

      this.callState = CALL_STATE_MAP[e.state] || CALL_STATE_MAP['1'];

      if (this.callState === 'Connecting') {
        if (!muteVideo && !isOnhold) {
          this.startCamera();

          // 初始入会时，设置本地画面是360P画面
          this.setLocalPreviewResolution(LocalPreviewResolution.normal360P);
          // 初始入会时，设置本地最大发送能力
          this.setVideoProfile({
            // 最大发送(编码)分辨率，720P
            maxResPvtx: 3,
            // 最大接收(解码)分辨率，720P
            maxResPvrx: 3,
            // 最大发送(编码)帧率，15帧
            maxFpsPvtx: 3,
            // 最大接收(解码)帧率， 15帧
            maxFpsPvrx: 3,
          });
        }
      }

      const lastIsOnhold = this.layoutManager.getIsOnHold();

      this.logger.log('callState onHold', {
        isOnhold,
        lastIsOnhold,
        callState: this.callState,
        muteCamera: this.config.muteVideo,
      });

      if (this.callState === 'Connected') {
        // const lastIsOnhold = this.layoutManager.getIsOnHold();
        const { meetingNumber, meetingId } = this.makeCallInfo;

        this.layoutManager.createLocalRender();

        if (muteAudio && !hasInMeeting) {
          this.layoutManager.setLocalLayoutStatus();
        }

        if (muteVideo && !hasInMeeting) {
          this.layoutManager.setLocalLayoutStatus();
          this.layoutManager.updateLocalRenderer(true);
        }

        // 如果从等候室变为离开等候室，是否打开摄像头取决于入会时的配置
        if (!isOnhold && lastIsOnhold) {
          this.muteCamera(muteVideo);
        }

        // 如果是进入了等候室，并且现在摄像头开着，那就关闭摄像头
        if (isOnhold && !lastIsOnhold && this.video === 'unmuteVideo') {
          this.stopCamera();
        }

        // 检查是否是主持人，会在CONF_HOST_CHANGED中回调，此版本之前不需要调用此方法，CONF_HOST_CHANGED会自动回调
        this.checkHostMeetingPermission(meetingNumber, meetingId);

        // 上报会议室信息
        if (e.displayName) {
          const { displayName, numberType, meetingNumber } = e;
          this.emit(EVENT.RTC_CONFERENCE_INFO, {
            displayName,
            numberType,
            meetingNumber,
          });
        }

        // 麦克风采集
        if (isOnhold) {
          this.stopAudioCapture();
        } else {
          this.startAudioCapture();
        }
      }

      this.emit(EVENT.RTC_EVENT_CALL_STATE, {
        state: this.callState,
        error: SDK_ERROR_MAP_V2[e.error] ?? e.error,
        reason,
        code: e.reason,
      });

      this.layoutManager.setIsOnhold(isOnhold);

      // 上报等候室信息
      this.emit(EVENT.RTC_ON_HOLD_INFO, {
        isOnhold,
        waitingReason,
      });
    });

    // content state
    this.sdk.registerEvent(EVENT.RTC_EVENT_CONTENT_STATE, (state: ShareContentState) => {
      this.logger.log('Get sdk event content-state: ', state);

      const { contentCaptureType, contentInfo } = this.sendContentParams;
      this.logger.log('send content params: ', this.sendContentParams);

      if (state === ShareContentState.SENDING) {
        this.contentSharing = true;
        this.sdk.startContentCapture(contentCaptureType, contentInfo, false);
      } else {
        if (state !== ShareContentState.RECEIVING && this.contentSharing) {
          this.logger.log('Stop content capture: ', state);
          this.stopSendContent();
        }
        this.contentSharing = false;
      }

      if (this.config.model === LayoutModel.AUTO) {
        this.layoutManager.setLocalShareContent(state !== ShareContentState.IDLE);
      }
      this.emit(EVENT.RTC_EVENT_CONTENT_STATE, state);
    });

    this.sdk.registerEvent(EVENT.RTC_EVENT_VIDEO_STREAMS, (streams: IRoster[]) => {
      this.logger.log('Get sdk event video-streams: ', streams);

      if (this.callState === 'Not_In_Call') {
        this.logger.warn('Not in call, reject video streams message.');

        this.emit(EVENT.RTC_EVENT_SDK_ERROR, getStatus('XYSDK:960112'));

        return;
      }

      let nextStreams = streams;

      // 如果是DM模式，移除sourceId，避免主sdk启动定时器渲染画面
      if (this.isSlaveMode) {
        nextStreams = nextStreams.map((item) => {
          const { exDeviceId, sourceId } = item;

          return { ...item, sourceId: exDeviceId ? '' : sourceId };
        });
      }

      this.layoutManager.setStreams(nextStreams);
      this.layoutManager.updateRenderers();
      this.layoutManager.createVideoStreamLayout();
    });

    this.sdk.registerEvent(EVENT.RTC_EVENT_KICK_OUT, (state: string) => {
      this.logger.log('Get sdk event kick out:', state);

      this.emit(EVENT.RTC_EVENT_KICK_OUT, state);
    });

    this.sdk.registerEvent(EVENT.RTC_EVENT_CONF_INFO_CHANGED, (e: IConfInfo) => {
      this.logger.log('Get sdk event conf info changed: ', e);

      console.log('this.model', this.config.model);
      // 自动布局会自动根据模板请流操作
      if (this.config.model === LayoutModel.AUTO) {
        this.layoutManager.setConfInfo(e);
        // 非等候室才请流
        if (!this.layoutManager.getIsOnHold()) {
          this.layoutManager.calcRequestLayout();
        }
      }

      const { participantCount = 0 } = e;
      // 会中人数大于4人时，本地画面分辨率将减低到180P
      let nextLocalRes = participantCount > 4 ? LocalPreviewResolution.low180P : LocalPreviewResolution.normal360P;

      if (nextLocalRes !== this.localPreviewResolution) {
        this.setLocalPreviewResolution(nextLocalRes);
      }

      // 上报会议变化消息
      this.emit(EVENT.RTC_EVENT_CONF_INFO_CHANGED, e);
    });

    /**
     * 回调VideoFrame数据，已废弃
     *
     * @deprecated
     */
    this.sdk.registerEvent(EVENT.RTC_EVENT_Video_Frame_Ready, (e: IVideoFrame) => {});

    this.sdk.registerEvent(EVENT.RTC_EVENT_MIC_ENERGY_REPORTED, (e: number) => {
      let level = e < 30 ? 30 : e > 100 ? 100 : e;
      const realLevel = Math.ceil(level / 0.6 - 50);

      if (this.audio === 'unmute') {
        this.emit(EVENT.RTC_EVENT_MIC_ENERGY_REPORTED, realLevel);
      }
    });

    this.sdk.registerEvent(EVENT.RTC_EVENT_SDK_ERROR, (e: string) => {
      this.logger.log('sdk event error: ', e);
      this.emit(EVENT.RTC_EVENT_SDK_ERROR, e);
    });

    this.sdk.registerEvent(EVENT.RTC_EVENT_CONF_CONTROL, (e: IConfControl) => {
      this.logger.log('sdk event conf control: ', e);

      const { muteMic, commonJson } = e;
      let rename = '';

      if (muteMic === 'mute' || muteMic === 'unmute') {
        this.audio = muteMic;
        this.sdk.muteMic(muteMic === 'mute');
      }

      try {
        const renameJson = JSON.parse(commonJson);
        const tmpName = JSON.parse(renameJson.rename).tmpName;
        rename = Base64.decode(tmpName);
      } catch (error) {}

      if (rename) {
        this.rename = rename;
      }

      if (muteMic === 'mute' || muteMic === 'unmute' || rename) {
        this.layoutManager.setLocalLayoutStatus();
      }

      this.emit(EVENT.RTC_EVENT_CONF_CONTROL, e);
    });

    this.sdk.registerEvent(EVENT.RTC_MEDIA_DEVICE_EVENT, (e: DeviceTypeMap) => {
      this.logger.log('sdk event media change device: ', e);

      this.deviceManager.onMediaDeviceEvent(e);
    });

    this.sdk.registerEvent(EVENT.RTC_NETWORK_INDICATOR_LEVEL, (e: number) => {
      this.logger.log('Get sdk event network indicator level: ', e);
      this.emit(EVENT.RTC_NETWORK_INDICATOR_LEVEL, e);
    });

    // onMicMuted： 0 false, 1 true
    this.sdk.registerEvent(EVENT.RTC_EVENT_MIC_MUTED, (e: IMicMuted) => {
      this.logger.log('Get sdk event sdk mic muted: ', e);
      this.emit(EVENT.RTC_EVENT_MIC_MUTED, e);
    });

    this.sdk.registerEvent(EVENT.RTC_EVENT_VIDEO_STATUS_CHANGED, (e: VideoStatus) => {
      this.logger.log('Get sdk event video status changed: ', e);
      this.emit(EVENT.RTC_EVENT_VIDEO_STATUS_CHANGED, e);
    });

    // onCallInvited
    this.sdk.registerEvent(EVENT.RTC_EVENT_CALL_INVITED, (e: ICallInvited) => {
      this.logger.log('Get sdk event call invited: ', e);
      this.emit(EVENT.RTC_EVENT_CALL_INVITED, e);
    });

    // onHowlingDetected: 0 false 无啸叫， 1 true 有啸叫
    this.sdk.registerEvent(EVENT.RTC_HOWLING_DETECTED, (e: IHowlingDetected) => {
      this.logger.log('Get sdk event howling detected: ', e);
      this.emit(EVENT.RTC_HOWLING_DETECTED, e);
    });

    // 字幕
    this.sdk.registerEvent(EVENT.RTC_SUB_TITLE, (e: ISubTitle) => {
      this.logger.log('Get sdk event sub title: ', e);
      this.emit(EVENT.RTC_SUB_TITLE, e);
    });
    // 出入会消息
    this.sdk.registerEvent(EVENT.RTC_IN_OUT_REMINDER, (e: IInOutReminder[]) => {
      this.logger.log('Get sdk event in and out reminder: ', e);
      let reminder = [];

      e.forEach((item) => {
        let extUserId = item.extUserId || '';
        const extUserIds = extUserId.split(':');

        if (extUserIds.length > 1) {
          extUserId = extUserIds[1];
        }

        reminder.push(Object.assign({}, item, { extUserId }));
      });

      this.logger.log('emit in and out reminder: ', reminder);

      this.emit(EVENT.RTC_IN_OUT_REMINDER, reminder);
    });

    this.sdk.registerEvent(EVENT.RTC_EVENT_EX_DEVICES_CHANGED, () => {
      this.logger.log('Get  sdk event RTC_EVENT_EX_DEVICES_CHANGED');

      const exDeviceList = this.sdk.getExDeviceList();
      this.remoteMasterMap = {};

      exDeviceList.forEach((item) => {
        if (!item.isLocal) {
          this.remoteMasterMap[item.deviceID] = item;
        }
      });

      this.logger.log('getExDeviceList list: ', exDeviceList);
      this.logger.log('remoteMasterMap: ', this.remoteMasterMap);

      this.emit(EVENT.RTC_EVENT_EX_DEVICES_CHANGED, this.remoteMasterMap);

      // 自动布局会自动根据模板请流操作
      if (this.config.model === LayoutModel.AUTO) {
        this.layoutManager.calcRequestLayout();
      }
    });

    /**
     * 人脸识别数据回调 (人脸信息、username、位置信息等)
     */
    this.sdk.registerEvent(EVENT.RTC_AI_FACE_RECV, (e: IAIFaceRecv) => {
      // this.logger.log("[event] Get ai face recv", e);

      if (e.isLocal) {
        const local = this.layoutManager.getLocal();

        e.calluri = local.roster.callUri;
      }

      this.emit(EVENT.RTC_AI_FACE_RECV, e);
    });

    /**
     * getFaceInfo回调(人脸信息，username等)
     *
     * @deprecated 已经集成到AIFaceRec事件中
     */
    this.sdk.registerEvent(EVENT.RTC_AI_FACE_INFO, (e: string) => {
      this.logger.log('[event] Get ai face info', e);

      let info: IAIFaceInfo[] | string = '';

      try {
        info = JSON.parse(e);
      } catch (error) {
        info = e;
      }

      this.emit(EVENT.RTC_AI_FACE_INFO, info);
    });

    // 开启/关闭云端录制状态监听
    this.sdk.registerEvent(EVENT.RTC_RECORD_STATE_CHANGED, (e: IRecordStateChange) => {
      this.logger.log('[event] Record state changed', e);
      // error不存在了，合并到了reason中
      let { reason: recordReason, recordState, error: errorInt } = e;

      let error = recordReason ? recordReason : errorInt ? 'XYSDK:96' + errorInt : '';

      const reason = SDK_ERROR_MAP_V2[error] ?? error;

      this.recordStatus = recordState;

      // 录制过程中，由于本人和会控都有可能操作，所以需要自己区分是否是本人在录制
      if (e.recordState === RecordStatus.ACTING) {
        this.isSelfRecord = true;
      } else if (e.recordState === RecordStatus.IDLE || e.recordState === RecordStatus.IDLE_BY_OTHERS) {
        this.isSelfRecord = false;
      }

      const message = SERVER_ERROR_MAP[locale][reason] || SDK_ERROR_MAP[locale][reason] || '';

      const result = {
        recordState,
        reason,
        message,
      };

      this.logger.log('emit Record state changed', result);

      this.emit(EVENT.RTC_RECORD_STATE_CHANGED, result);
    });

    this.sdk.registerEvent(EVENT.RTC_HTTP_RESPONSE, (e: IResponse) => {
      this.logger.log('[event] Get http response', e);

      this.emit(EVENT.RTC_HTTP_RESPONSE, e);
    });

    this.sdk.registerEvent(EVENT.RTC_MEETING_MUTE_QUERY, (e: IMeetingMuteQuery) => {
      this.logger.log('[event] Get meeting mute query', e);

      if (e.isMuteCamera) {
        // 兼容老版本
        this.emit(EVENT.RTC_MEETING_MUTE_QUERY, { key: 'muteVideoQuery' });
      }
    });

    // 日志上传回调
    this.sdk.registerEvent(EVENT.LOG_UPLOAD_RESULT, (e: IRestApiResult) => {
      this.logger.log('[event] Get log upload result', e);

      e.code = SDK_ERROR_MAP_V2[e.code] ?? e.code;

      this.emit(EVENT.LOG_UPLOAD_RESULT, e);
    });

    // 获取主持会议的相关配置信息和状态回调 需要调用checkHostMeetingPermission
    this.sdk.registerEvent(EVENT.CONF_HOST_CHANGED, (e: IConfHost) => {
      this.logger.log('[event] Get conf host changed', e);
      // 兼容老版本
      this.emit(EVENT.CONF_HOST_CHANGED, {
        isHost: e.isHost,
      });
    });

    // 主持会议权限变更
    this.sdk.registerEvent(EVENT.HOST_AUTHORITY, (e: boolean) => {
      this.logger.log('[event] Get conf host authority changed', e);
      // 兼容老版本
      this.emit(EVENT.CONF_HOST_CHANGED, {
        isHost: e,
      });
    });

    // 联席主持人权限变更
    this.sdk.registerEvent(EVENT.CO_CHAR_AUTHORITY, (e: boolean) => {
      this.logger.log('[event] Get conf char authority changed', e);
      // 兼容老版本
      this.emit(EVENT.CONF_HOST_CHANGED, {
        isHost: e,
      });
    });

    // 取消举手
    this.sdk.registerEvent(EVENT.CONF_HAND_UP_CANCELLED, () => {
      this.logger.log('Get sdk conf hand up cancelled');
      this.emit(EVENT.CONF_HAND_UP_CANCELLED, true);
    });

    // 互动工具回调
    this.sdk.registerEvent(EVENT.INTERACTIVE_TOOL_INFO, (e: any) => {
      this.logger.log('[event] Get interactive tool info', e);

      if (!e.isSupport) {
        return;
      }

      const webViewUrl = e.startWebViewUrl;

      // 兼容老版本, startWebViewUrl改为旧版本的webViewUrl
      delete e.startWebViewUrl;

      // 3.3 c++ 时，这一部分是放在c++层面，3.8c++后需要js自行处理, 这里为兼容老版本 放到js sdk内部处理
      if (e.eventType === EventType.START) {
        // 记录processType, 因为当event为STOP,PUBLISH,RESULT_CLOSE时, processType可能是NONE
        this.interactiveToolProcessType = e.processType;
      } else if (e.processType === ProcessType.NONE) {
        // processType为NONE时无法分辨当前是在处理什么业务，因此这里需要做修改
        e.processType = this.interactiveToolProcessType;
      }

      const copyWritingArr = e.copywriting;
      let copywriting: ICopywriting = {
        dialogTitle: '',
        dialogContent: '',
        dialogSubContent: '',
        dialogLabel: '',
        notifyContent: '',
        notifyLabel: '',
      };

      copyWritingArr.forEach((writingItem) => {
        if (writingItem.indexNo === 1 || (e.eventType === EventType.START && writingItem.indexNo <= 6) || (e.eventType === EventType.STOP && writingItem.indexNo >= 8))
          copywriting[ENUM_COPY_WRITING[writingItem.indexNo]] = writingItem.content;
      });

      this.logger.log('[event] Get sdk interactive tool final info: ', e);
      this.emit(EVENT.INTERACTIVE_TOOL_INFO, { ...e, copywriting, webViewUrl });
    });

    // 签到结果上报
    this.sdk.registerEvent(EVENT.SUBMIT_SIGNATURE_RESULT, (e: ISignInfo) => {
      this.logger.log('[event] Get sdk submit signature result: ', e);
      e.code = SDK_ERROR_MAP_V2[e.code] ?? e.code;

      // 兼容老版本
      if (e.code === SUCCESS_CODE) {
        e.code = 0;
      }

      this.emit(EVENT.SUBMIT_SIGNATURE_RESULT, {
        code: e.code,
        status: e.status,
      });
    });

    // 是否支持同传字幕上报
    this.sdk.registerEvent(EVENT.SUPPORT_AI_CAPTION_RESULT, (e: boolean) => {
      this.logger.log('[event] Get sdk support api caption result: ', e);

      this.isSupportAiCaption = e;

      this.emit(EVENT.SUPPORT_AI_CAPTION_RESULT, e);
    });

    // 字幕内容上报
    this.sdk.registerEvent(EVENT.AI_CAPTION_INFO_CHANGED, (e: IAiCaptionInfo) => {
      this.logger.log('[event] Get sdk ai caption changed: ', e);
      this.emit(EVENT.AI_CAPTION_INFO_CHANGED, e);
    });

    // 会控H5链接
    this.sdk.registerEvent(EVENT.HOST_MEETING_URL, (url: string) => {
      this.logger.log('[event] Get sdk host meeting url: ', url);

      let mobileUrl = '';
      let membersUrl = '';

      this.logger.log('get ConfMgmtUrl from sdk:', url);

      if (typeof url === 'string') {
        // 添加参数from=sdk
        const urlObj = new URL(url);
        urlObj.searchParams.append('from', 'sdk');
        url = urlObj.toString();

        mobileUrl = url.replace('&platform=pc', '&platform=app');
        membersUrl = url.replace('/cloudConf', '/cloudConf/members');
      }

      this.logger.log('ConfMgmtUrl:', {
        pc: url,
        mobile: mobileUrl,
        members: membersUrl,
      });

      this.emit(EVENT.HOST_MEETING_URL, {
        pc: url,
        mobile: mobileUrl,
        members: membersUrl,
      });
    });

    //  共享 App 采集状态
    this.sdk.registerEvent(EVENT.APP_WINDOW_CAPTURE_STATE, (e: IAppWindowCaptureState) => {
      this.logger.log('[event] Get App sharing window state: ', e);
      this.emit(EVENT.APP_WINDOW_CAPTURE_STATE, e);
    });

    // 只有处理批注工具栏的层级才用到这个事件 暂时用不到
    this.sdk.registerEvent(EVENT.APP_WINDOW_CAPTURE_STATE_NO_EXTERNAL_SCREEN, (e: IAppWindowCaptureState) => {
      this.logger.log('[event] Get App sharing window state without external screen: ', e);
      this.emit(EVENT.APP_WINDOW_CAPTURE_STATE_NO_EXTERNAL_SCREEN, e);
    });

    // 批注开始
    this.sdk.registerEvent(EVENT.ANNOTATION_START, () => {
      this.logger.log('[event] Get AnnotationStart');
      this.emit(EVENT.ANNOTATION_START);
    });

    // 批注结束
    this.sdk.registerEvent(EVENT.ANNOTATION_STOP, () => {
      this.logger.log('[event] Get onAnnotationStop');
      this.emit(EVENT.ANNOTATION_STOP);
    });

    // 批注清除
    this.sdk.registerEvent(EVENT.ANNOTATION_CLEAN, () => {
      this.logger.log('[event] Get AnnotationClean');
      this.emit(EVENT.ANNOTATION_CLEAN);
    });

    // 接收批注线条
    this.sdk.registerEvent(EVENT.ANNOTATION_RECEIVER_LINE, (line: IReceiveLine) => {
      this.logger.log('[event] Get AnnotationReceiveLine', line);
      this.emit(EVENT.ANNOTATION_RECEIVER_LINE, line);
    });
  }
}

export { XYRTC };
